import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { xinyiGuide } from './content'
import type { Lang } from './types'

interface XinyiGuideV2Props {
  currentLang: Lang
}

/**
 * XinyiGuideV2
 * Expandable "Xinyi Coffee Quick Guide" — left/right label-value rows,
 * three minimal CTAs at the bottom (linking to V2 shop / store routes).
 */
const XinyiGuideV2: React.FC<XinyiGuideV2Props> = ({ currentLang }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <div className="mb-8 text-center">
          <p
            className="mb-3 text-[12px] tracking-[0.22em] text-black/65"
            style={{
              fontFamily: "'EB Garamond', serif",
              fontStyle: 'italic',
              fontWeight: 400,
            }}
          >
            {xinyiGuide.eyebrow[currentLang]}
          </p>
          <h2
            className="text-black"
            style={{
              fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
              fontWeight: 400,
              fontSize: 'clamp(1.5rem, 2.6vw, 2rem)',
              letterSpacing: '0.04em',
              lineHeight: 1.3,
            }}
          >
            {xinyiGuide.title[currentLang]}
          </h2>
        </div>

        <div className="border border-black/15">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-black/[0.02]"
          >
            <span
              className="text-sm tracking-[0.18em] text-black"
              style={{ fontFamily: "'EB Garamond', 'Noto Serif TC', serif" }}
            >
              Ongu Studio - Coffee Roasting - 信義
            </span>
            <span className="text-xl text-black/50">{expanded ? '−' : '+'}</span>
          </button>

          {expanded && (
            <div className="border-t border-black/10 px-6 py-10 md:px-10 md:py-12">
              <dl
                className="grid grid-cols-1 gap-6 md:grid-cols-2"
                style={{
                  fontFamily: "'Noto Serif TC', sans-serif",
                  fontWeight: 300,
                  letterSpacing: '0.03em',
                }}
              >
                {xinyiGuide.rows.map((row, i) => (
                  <div key={i}>
                    <dt className="mb-2 text-[11px] tracking-[0.22em] text-black/55 uppercase">
                      {row.label[currentLang]}
                    </dt>
                    <dd className="text-[14px] leading-relaxed text-black/80">
                      {row.href ? (
                        <a
                          href={row.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="border-b border-black/30 pb-0.5 transition-colors hover:border-black"
                        >
                          {row.value[currentLang]}
                        </a>
                      ) : (
                        row.value[currentLang]
                      )}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="mt-10 grid gap-3 md:grid-cols-3">
                {xinyiGuide.ctas.map((cta, i) => (
                  <Link
                    key={i}
                    to={`/${currentLang}${cta.href}`}
                    onClick={() => window.scrollTo(0, 0)}
                    className="border border-black px-5 py-3 text-center text-[12px] tracking-[0.22em] text-black transition-colors hover:bg-black hover:text-white"
                    style={{ fontFamily: "'EB Garamond', serif" }}
                  >
                    {cta.label[currentLang].toUpperCase()}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default XinyiGuideV2
